import { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, IconButton,
  Button, CircularProgress, Alert, Snackbar, Grid, TextField,
  Slider, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip
} from '@mui/material';
import {
  ArrowBack, Restaurant, LocalHospital, Favorite, Add, Save, Cancel, PersonPin, AddCircle, ExitToApp, Assignment
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { dayCareAPI, prognosisAPI } from '../../services/api';
import DietPlanManagement from './DietPlanManagement';
import TreatmentPlanManagement from './TreatmentPlanManagement';

// ─── Inline Vitals Tab ────────────────────────────────────────────────────────
function DayCareVitalsTab({ dayCareId, patientId, filterFrom, filterTo }) {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    bloodPressure: null, temperature: null, pulseRate: null, respiratoryRate: null,
    weight: null, height: null, oxygenSaturation: null, rbs: null, insulinUnits: null,
    painScale: 0, notes: ''
  });

  useEffect(() => { loadVitals(); }, [dayCareId]);

  const loadVitals = async () => {
    setLoading(true);
    try {
      const res = await dayCareAPI.getVitals(dayCareId);
      setVitals(Array.isArray(res.data.data) ? res.data.data : []);
    } catch { setError('Failed to load vitals'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!patientId) { setError('Patient ID missing. Cannot record vitals.'); return; }
    setSaving(true);
    try {
      await dayCareAPI.recordVitals(dayCareId, patientId, form);
      setSuccess('Vitals recorded successfully');
      setAddOpen(false);
      setForm({ bloodPressure: '', temperature: '', pulseRate: '', respiratoryRate: '', weight: '', height: '', oxygenSaturation: '', rbs: '', insulinUnits: '', painScale: 0, notes: '' });
      loadVitals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record vitals');
    } finally { setSaving(false); }
  };

  const fmt = (str) => new Date(str).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const displayVitals = (filterFrom || filterTo)
    ? vitals.filter(v => { const d = (v.recorded_at || v.created_at || '').slice(0, 10); return (!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo); })
    : vitals;

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="error" startIcon={<Add />} onClick={() => setAddOpen(true)}>Record Vitals</Button>
      </Box>
      {displayVitals.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Favorite sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">{(filterFrom || filterTo) ? 'No vitals recorded for this period' : 'No vitals recorded yet'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {['Date/Time', 'BP', 'Temp (°F)', 'Pulse', 'RR', 'SpO2 (%)', 'Weight (kg)', 'RBS', 'Insulin (U)', 'Pain'].map(h => (
                  <TableCell key={h}><strong>{h}</strong></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayVitals.map((v, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{fmt(v.recorded_at || v.created_at)}</TableCell>
                  <TableCell>{v.blood_pressure || '-'}</TableCell>
                  <TableCell>{v.temperature || '-'}</TableCell>
                  <TableCell>{v.pulse_rate || '-'}</TableCell>
                  <TableCell>{v.respiratory_rate || '-'}</TableCell>
                  <TableCell>{v.oxygen_saturation || '-'}</TableCell>
                  <TableCell>{v.weight || '-'}</TableCell>
                  <TableCell>{v.rbs || '-'}</TableCell>
                  <TableCell>{v.insulin_units || '-'}</TableCell>
                  <TableCell>
                    {v.pain_scale !== null && v.pain_scale !== undefined
                      ? <Chip label={v.pain_scale} size="small" color={v.pain_scale >= 7 ? 'error' : v.pain_scale >= 4 ? 'warning' : 'success'} />
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Record Vitals</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {[
              ['Blood Pressure', 'bloodPressure', 'text', 'e.g. 120/80'],
              ['Temperature (°F)', 'temperature', 'number'],
              ['Pulse Rate (bpm)', 'pulseRate', 'number'],
              ['Respiratory Rate (/min)', 'respiratoryRate', 'number'],
              ['Weight (kg)', 'weight', 'number'],
              ['Height (cm)', 'height', 'number'],
              ['SpO2 (%)', 'oxygenSaturation', 'number'],
              ['RBS (mg/dL)', 'rbs', 'number'],
              ['Insulin Units', 'insulinUnits', 'number'],
            ].map(([label, key, type, placeholder]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <TextField fullWidth label={label} type={type} placeholder={placeholder} size="small"
                  value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>Pain Scale: {form.painScale}</Typography>
              <Slider value={form.painScale} onChange={(_, val) => setForm(f => ({ ...f, painScale: val }))}
                min={0} max={10} step={1} marks valueLabelDisplay="auto"
                color={form.painScale >= 7 ? 'error' : form.painScale >= 4 ? 'warning' : 'success'} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" size="small"
                value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="error"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />} disabled={saving}>
            {saving ? 'Saving...' : 'Save Vitals'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Inline Prognosis Tab ─────────────────────────────────────────────────────
function DayCarePrognosisTab({ dayCareId, patientId, filterFrom, filterTo }) {
  const [prognosis, setPrognosis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    progressiveNote: '',
    doctorsOrder: '',
    recordedAt: ''
  });

  useEffect(() => {
    loadPrognosis();
  }, [dayCareId]);

  const loadPrognosis = async () => {
    setLoading(true);
    try {
      const response = await prognosisAPI.getDayCare(dayCareId);
      const data = response.data.data || response.data;
      setPrognosis(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load prognosis history');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!patientId) {
      setError('Patient ID missing. Cannot record prognosis.');
      return;
    }
    if (!form.progressiveNote && !form.doctorsOrder) {
      setError('Please provide at least a Progressive Note or a Doctor\'s Order.');
      return;
    }
    setSaving(true);
    try {
      await prognosisAPI.recordDayCare(dayCareId, patientId, {
        ...form,
        recordedAt: form.recordedAt || undefined
      });
      setSuccess('Prognosis recorded successfully');
      setAddOpen(false);
      setForm({
        progressiveNote: '',
        doctorsOrder: '',
        recordedAt: ''
      });
      loadPrognosis();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record prognosis');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (str) => new Date(str).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const displayPrognosis = (filterFrom || filterTo)
    ? prognosis.filter(p => {
        const d = (p.recorded_at || p.created_at || '').slice(0, 10);
        return (!filterFrom || d >= filterFrom) && (!filterTo || d <= filterTo);
      })
    : prognosis;

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="error" startIcon={<Add />} onClick={() => setAddOpen(true)}>
          Record Prognosis
        </Button>
      </Box>

      {displayPrognosis.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">{(filterFrom || filterTo) ? 'No prognosis records for this period' : 'No prognosis records yet'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell style={{ width: '150px' }}><strong>Date/Time</strong></TableCell>
                <TableCell><strong>Progressive Note</strong></TableCell>
                <TableCell><strong>Doctor's Order</strong></TableCell>
                <TableCell style={{ width: '150px' }}><strong>Recorded By</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayPrognosis.map((p, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{fmt(p.recorded_at || p.created_at)}</TableCell>
                  <TableCell style={{ whiteSpace: 'pre-line' }}>{p.progressive_note || '-'}</TableCell>
                  <TableCell style={{ whiteSpace: 'pre-line' }}>{p.doctors_order || '-'}</TableCell>
                  <TableCell>
                    {p.recorded_by_name || '-'}
                    {p.recorded_by_role && <Typography variant="caption" display="block" color="text.secondary">{p.recorded_by_role}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Prognosis Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Daily Prognosis</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date & Time (Optional)"
                type="datetime-local"
                value={form.recordedAt}
                onChange={e => setForm(f => ({ ...f, recordedAt: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Progressive Note"
                value={form.progressiveNote}
                onChange={e => setForm(f => ({ ...f, progressiveNote: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Doctor's Order"
                value={form.doctorsOrder}
                onChange={e => setForm(f => ({ ...f, doctorsOrder: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="error"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={saving}>
            {saving ? 'Saving...' : 'Save Prognosis'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Patient Info + Clinical Notes Tab ───────────────────────────────────────
function DayCarePatientInfoTab({ dayCareId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    chiefComplaint: '', diagnosis: '', pastMedicalHistory: '',
    currentMedications: '', familyHistory: '', doctorNotes: ''
  });

  useEffect(() => { load(); }, [dayCareId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await dayCareAPI.getRegistrationDetails(dayCareId);
      const d = res.data.data;
      setData(d);
      setForm({
        chiefComplaint:     d.chief_complaint      || '',
        diagnosis:          d.diagnosis            || '',
        pastMedicalHistory: d.past_medical_history || '',
        currentMedications: d.current_medications  || '',
        familyHistory:      d.family_history       || '',
        doctorNotes:        d.doctor_notes         || '',
      });
    } catch { setError('Failed to load patient details'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dayCareAPI.updateClinicalNotes(dayCareId, form);
      setSuccess('Clinical notes saved successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save notes');
    } finally { setSaving(false); }
  };

  const f = (key) => ({ value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) });

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      {/* Patient demographics */}
      {data && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="secondary" gutterBottom>
            Patient Information
          </Typography>
          <Grid container spacing={1.5}>
            {[
              ['Name',         `${data.first_name} ${data.last_name}`],
              ['UHID',         data.patient_code],
              ['Age / Gender', `${data.age ?? '—'} yrs / ${data.gender || '—'}`],
              ['Phone',        data.phone || '—'],
              ['Email',        data.email || '—'],
              ['Address',      data.address || '—'],
              ['Start Date',   new Date(data.start_date).toLocaleDateString('en-IN')],
              ['Days',         `${data.days_attended} / ${data.planned_days || '—'}`],
              ['Reason',       data.reason || '—'],
            ].map(([label, val]) => (
              <Grid item xs={12} sm={6} md={4} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight="medium">{val}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Editable clinical notes */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="secondary">
            Clinical Notes
          </Typography>
          <Button variant="contained" color="secondary" size="small"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Notes'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Chief Complaint"
              placeholder="Primary reason for day care…" {...f('chiefComplaint')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Diagnosis"
              placeholder="Clinical diagnosis…" {...f('diagnosis')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Past Medical History"
              placeholder="Previous illnesses, surgeries…" {...f('pastMedicalHistory')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={3} label="Current Medications"
              placeholder="Ongoing medications and dosage…" {...f('currentMedications')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Family History"
              placeholder="Relevant family medical history…" {...f('familyHistory')} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline rows={2} label="Doctor's Notes"
              placeholder="Additional clinical observations…" {...f('doctorNotes')} />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

// ─── Main DayCarePatientManager ───────────────────────────────────────────────
function DayCarePatientManager() {
  const { admissionId: dayCareId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientInfo = location.state || {};
  const [activeTab, setActiveTab] = useState(0);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const [extendDialog, setExtendDialog] = useState({ open: false, additionalDays: 1 });
  const [extendLoading, setExtendLoading] = useState(false);
  const [extendError, setExtendError] = useState('');
  const [plannedDays, setPlannedDays] = useState(patientInfo.plannedDays || 0);

  const [dischargeDialog, setDischargeDialog] = useState(false);
  const [dischargeLoading, setDischargeLoading] = useState(false);

  const handleExtend = async () => {
    setExtendLoading(true);
    setExtendError('');
    try {
      const res = await dayCareAPI.extendDuration(dayCareId, { additionalDays: extendDialog.additionalDays });
      setPlannedDays(res.data.data?.plannedDays || plannedDays + extendDialog.additionalDays);
      setExtendDialog({ open: false, additionalDays: 1 });
    } catch (err) {
      setExtendError(err.response?.data?.message || 'Failed to extend duration');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleDischarge = async () => {
    setDischargeLoading(true);
    try {
      await dayCareAPI.discharge(dayCareId);
      navigate('/doctor', { state: { activeTab: 5 } });
    } catch (err) {
      setDischargeLoading(false);
      setDischargeDialog(false);
    }
  };

  const minDate = patientInfo.admissionDate
    ? new Date(patientInfo.admissionDate).toISOString().split('T')[0]
    : undefined;
  const maxDate = patientInfo.admissionDate && plannedDays
    ? (() => { const d = new Date(patientInfo.admissionDate); d.setDate(d.getDate() + plannedDays - 1); return d.toISOString().split('T')[0]; })()
    : undefined;

  const hasFilter = filterFrom || filterTo;

  return (
    <Box>
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/doctor', { state: { activeTab: 5 } })} sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {patientInfo.patientName || 'Day Care Patient'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UHID: {patientInfo.patientCode || '-'}
                  {patientInfo.admissionDate && ` • Start: ${new Date(patientInfo.admissionDate).toLocaleDateString('en-GB')}`}
                  {plannedDays > 0 && ` • ${plannedDays} days`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {activeTab !== 0 && (
                <>
                  <TextField label="From" type="date" size="small" value={filterFrom}
                    onChange={e => setFilterFrom(e.target.value)} InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minDate, max: filterTo || maxDate }} sx={{ width: 160 }} />
                  <TextField label="To" type="date" size="small" value={filterTo}
                    onChange={e => setFilterTo(e.target.value)} InputLabelProps={{ shrink: true }}
                    inputProps={{ min: filterFrom || minDate, max: maxDate }} sx={{ width: 160 }} />
                  {hasFilter && <Button size="small" onClick={() => { setFilterFrom(''); setFilterTo(''); }}>Clear</Button>}
                </>
              )}
              <Button variant="outlined" size="small" color="primary" startIcon={<AddCircle />}
                onClick={() => setExtendDialog({ open: true, additionalDays: 1 })}>
                Extend
              </Button>
              <Button variant="outlined" size="small" color="error" startIcon={<ExitToApp />}
                onClick={() => setDischargeDialog(true)}>
                Discharge
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} textColor="secondary" indicatorColor="secondary">
          <Tab icon={<PersonPin />} iconPosition="start" label="Patient Info" />
          <Tab icon={<Restaurant />} iconPosition="start" label="Diet Plan" />
          <Tab icon={<LocalHospital />} iconPosition="start" label="Treatment Plan" />
          <Tab icon={<Favorite />} iconPosition="start" label="Vitals" />
          <Tab icon={<Assignment />} iconPosition="start" label="Prognosis" />
        </Tabs>
      </Box>

      <Box sx={{ pt: 2 }}>
        {activeTab === 0 && <DayCarePatientInfoTab dayCareId={dayCareId} />}
        {activeTab === 1 && <DietPlanManagement embedded apiOverride={dayCareAPI} filterFrom={filterFrom} filterTo={filterTo} />}
        {activeTab === 2 && <TreatmentPlanManagement embedded apiOverride={dayCareAPI} filterFrom={filterFrom} filterTo={filterTo} />}
        {activeTab === 3 && <DayCareVitalsTab dayCareId={dayCareId} patientId={patientInfo.patientId} filterFrom={filterFrom} filterTo={filterTo} />}
        {activeTab === 4 && <DayCarePrognosisTab dayCareId={dayCareId} patientId={patientInfo.patientId} filterFrom={filterFrom} filterTo={filterTo} />}
      </Box>

      {/* Extend Duration dialog */}
      <Dialog open={extendDialog.open} onClose={() => setExtendDialog({ open: false, additionalDays: 1 })} maxWidth="xs" fullWidth>
        <DialogTitle>Extend Day Care Duration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Patient: <strong>{patientInfo.patientName || 'Patient'}</strong><br />
            Current planned days: <strong>{plannedDays}</strong>
          </Typography>
          {extendError && <Alert severity="error" sx={{ mb: 2 }}>{extendError}</Alert>}
          <TextField
            fullWidth label="Additional Days" type="number"
            value={extendDialog.additionalDays}
            onChange={e => setExtendDialog(d => ({ ...d, additionalDays: Math.max(1, parseInt(e.target.value) || 1) }))}
            inputProps={{ min: 1 }}
          />
          {extendDialog.additionalDays > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              New total: {plannedDays + extendDialog.additionalDays} days
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog({ open: false, additionalDays: 1 })}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleExtend}
            disabled={extendLoading || extendDialog.additionalDays < 1}>
            {extendLoading ? <CircularProgress size={20} color="inherit" /> : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Confirm dialog */}
      <Dialog open={dischargeDialog} onClose={() => setDischargeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discharge Patient</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to discharge <strong>{patientInfo.patientName || 'this patient'}</strong> from Day Care?
            <br /><br />
            This will mark the registration as completed and set today as the discharge date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDischargeDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDischarge} disabled={dischargeLoading}>
            {dischargeLoading ? <CircularProgress size={20} color="inherit" /> : 'Discharge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DayCarePatientManager;
